import * as React from 'react';
import { Button, Card, CardBody, CardTitle, Grid, GridItem, Label, Title } from '@patternfly/react-core';
import { compareRows } from '../../../../shared/riskregister/sorting';
import type { Payload, Row } from '../../../../shared/riskregister/types';

function needsAttention(row: Row, payload: Pick<Payload, 'stalled_days' | 'sharp_score'>): boolean {
  return (
    row.open &&
    (row.due_status === 'overdue' ||
      row.idle >= payload.stalled_days ||
      (row.src === 'measured' && row.score !== null && row.score >= payload.sharp_score))
  );
}

export const NeedsAttentionPanel: React.FunctionComponent<{
  payload: Payload;
  rows: Row[];
}> = ({ payload, rows }) => {
  const [showAll, setShowAll] = React.useState(false);
  const attention = React.useMemo(
    () => rows.filter((row) => needsAttention(row, payload)).sort(compareRows('worst')),
    [payload, rows],
  );

  if (!attention.length) {
    return (
      <p className="risk-register__empty">
        No open item in this view is overdue, stalled, or assessed at the immediate-attention threshold.
      </p>
    );
  }

  const visibleAttention = showAll ? attention : attention.slice(0, 10);

  return (
    <>
      <Grid hasGutter>
        {visibleAttention.map((row) => {
          const overdue = row.due_status === 'overdue';
          const stalled = row.idle >= payload.stalled_days;
          const critical = row.src === 'measured' && row.score !== null && row.score >= payload.sharp_score;
          return (
            <GridItem key={row.key} span={12} md={6}>
              <Card
                isCompact
                className={`risk-register__attention-card risk-register__attention-card--${overdue || critical ? 'danger' : 'warning'}`}
              >
                <CardTitle>
                  <Title headingLevel="h3" size="md">
                    <a href={`${payload.jira}${row.key}`} target="_blank" rel="noreferrer">
                      {row.key}
                    </a>
                  </Title>
                </CardTitle>
                <CardBody>
                  <p>{row.summary}</p>
                  {overdue && (
                    <Label color="red" isCompact>
                      Overdue: {Math.abs(row.due_days || 0)} days past due
                    </Label>
                  )}
                  {stalled && (
                    <Label color="orange" isCompact>
                      Stalled: no status movement for {row.idle} days
                    </Label>
                  )}
                  {critical && (
                    <Label color="red" isCompact>
                      Immediate attention: assessed risk score {row.score}
                    </Label>
                  )}
                  <p>
                    {row.assignee || 'Unassigned'} · {row.svp} · {row.status}
                  </p>
                </CardBody>
              </Card>
            </GridItem>
          );
        })}
      </Grid>
      {attention.length > 10 && (
        <Button variant="link" isInline onClick={() => setShowAll((current) => !current)}>
          {showAll ? 'Show top 10' : `Show ${attention.length - 10} more`}
        </Button>
      )}
    </>
  );
};
